import { defineStore } from "pinia";

import { DEFAULT_API_ERROR_MESSAGE } from "@/common/constants";
import { errorTypeToMessage } from "@/common/mappers";
import {
  Arbetsgivarearende,
  ArendeType,
  ArendenByOrganisationsnummerRequest,
  type IInternKommentar,
  type IKundserviceAppApiClient,
  KanalType,
  KundgruppType,
  LamnaOverType,
  SimpleArbetsgivarearende,
  ValueResult_1OfGuid,
} from "@/composables/KundserviceAppApiClient";
import type { ApiError } from "@/types/ApiError";

export type State = {
  arende: ArendeState;
  openArenden: PaginatedArendenState;
  closedArenden: PaginatedArendenState;
  internKommentar: InternKommentarState;
  isLoading: boolean;
  error: ApiError | null;
};

type ArendeState = {
  isLoading: boolean;
  isSaving: boolean;
  data: Arbetsgivarearende | null;
  error: ApiError | null;
};

type PaginatedArendenState = {
  isLoading: boolean;
  arenden: SimpleArbetsgivarearende[];
  nextLink?: string | null;
  count: number;
  error: ApiError | null;
};

type InternKommentarState = {
  isSaving: boolean;
  data: IInternKommentar | null;
  error: string | null;
};

export type CreateArendeInfo = {
  oragnisationsnummer: string;
  puzzelId: string | undefined;
  kontaktorsakId: string;
  kontaktorsakNamn: string;
  fortydligadKontaktorsak: string;
  samtalsamneIds: string[];
  internalNote: string;
  isVerified: boolean;
  isClosed: boolean;
  kundgrupp: KundgruppType;
  arendetyp: ArendeType;
  lamnaOverTill: LamnaOverType;
  kanal: KanalType;
  ombud: boolean;
  namn: string | undefined;
  email: string | undefined;
  telefonnummer: string | undefined;
};

export type UpdateArendeInfo = {
  arendeNumber: string;
  puzzelId: string | undefined;
  samtalsamneIds: string[];
  internKommentar: string;
  isClosed: boolean;
  arendetyp: ArendeType;
  lamnaOverTill: LamnaOverType;
  namn: string | undefined;
  email: string | undefined;
  telefonnummer: string | undefined;
};

export type ReopenArendeInfo = {
  arendeNumber: string;
  isVerified: boolean;
  isManuallyVerified: boolean;
};

export type CreateUnlinkedArendeInfo = {
  puzzelId: string | undefined;
  kontaktorsakId: string;
  kontaktorsakNamn: string;
  fortydligadKontaktorsak: string;
  samtalsamneIds: string[];
  internalNote: string;
  isClosed: boolean;
  kundgrupp: KundgruppType;
  arendetyp: ArendeType;
  lamnaOverTill: LamnaOverType;
  kanal: KanalType;
  ombud: boolean;
};

export const useArbetsgivarearendenStore = defineStore("arbetsgivareArenden", {
  state: (): State => {
    return {
      arende: {
        isLoading: false,
        isSaving: false,
        data: null,
        error: null,
      },
      openArenden: {
        isLoading: false,
        arenden: [],
        nextLink: null,
        count: 0,
        error: null,
      },
      closedArenden: {
        isLoading: false,
        arenden: [],
        nextLink: null,
        count: 0,
        error: null,
      },
      internKommentar: {
        isSaving: false,
        data: null,
        error: null,
      },
      isLoading: false,
      error: null,
    };
  },
  getters: {
    arendenCount(state) {
      return state.openArenden.count + state.closedArenden.count;
    },
    hasArenden(state) {
      return (
        state.openArenden.arenden.length > 0 ||
        state.closedArenden.arenden.length > 0
      );
    },
  },
  actions: {
    async fetchInitialArenden(
      organisationsnummer: string,
      apiClient: IKundserviceAppApiClient,
    ) {
      try {
        this.$patch((store) => {
          store.isLoading = true;
          store.error = null;
        });

        const openArendenRequest =
          apiClient.getArbetsgivarearendenByOrganisationsnummer({
            organisationsnummer,
            top: 5,
            open: true,
          } as ArendenByOrganisationsnummerRequest);
        const closedArendenRequest =
          apiClient.getArbetsgivarearendenByOrganisationsnummer({
            organisationsnummer,
            top: 5,
            open: false,
          } as ArendenByOrganisationsnummerRequest);

        const [openArendenResponse, closedArendenResponse] = await Promise.all([
          openArendenRequest,
          closedArendenRequest,
        ]);

        this.$patch((store) => {
          store.openArenden.arenden = openArendenResponse.value;
          store.openArenden.nextLink = openArendenResponse.nextLink;
          store.openArenden.count = openArendenResponse.count ?? 0;

          store.closedArenden.arenden = closedArendenResponse.value;
          store.closedArenden.nextLink = closedArendenResponse.nextLink;
          store.closedArenden.count = closedArendenResponse.count ?? 0;
        });
      } catch (e) {
        this.$patch((store) => {
          store.error = e || "Error.";
        });
      } finally {
        this.$patch((store) => {
          store.isLoading = false;
        });
      }
    },
    async fetchArenden(
      organisationsnummer: string,
      open: boolean,
      top: number,
      apiClient: IKundserviceAppApiClient,
    ) {
      const arendeType = open ? "openArenden" : "closedArenden";

      try {
        this.$patch((store) => {
          store[arendeType].isLoading = true;
          store[arendeType].error = null;
        });

        const arendenResponse =
          await apiClient.getArbetsgivarearendenByOrganisationsnummer({
            organisationsnummer,
            top: top,
            nextLink: this.$state[arendeType].nextLink ?? undefined,
            open,
          } as ArendenByOrganisationsnummerRequest);

        this.$patch((store) => {
          store[arendeType].arenden.push(...arendenResponse.value);
          store[arendeType].nextLink = arendenResponse.nextLink;
        });
      } catch (e) {
        this.$patch((store) => {
          store[arendeType].error = e || "Error.";
        });
      } finally {
        this.$patch((store) => {
          store[arendeType].isLoading = false;
        });
      }
    },
    async fetchArende(
      arendeNumber: string,
      apiClient: IKundserviceAppApiClient,
    ) {
      try {
        this.$patch((store) => {
          store.arende.isLoading = true;
          store.arende.error = null;
        });
        const arende =
          await apiClient.getArbetsgivarearendeByArendeNumber(arendeNumber);
        this.$patch((store) => {
          store.arende.data = arende;
        });
        return arende;
      } catch (e) {
        this.$patch((store) => {
          store.arende.isLoading = false;
          store.arende.error = e || "Error.";
        });
      } finally {
        this.$patch((store) => {
          store.arende.isLoading = false;
        });
      }
    },
    async createArende(
      creationInfo: CreateArendeInfo,
      apiClient: IKundserviceAppApiClient,
    ) {
      try {
        this.$patch((store) => {
          store.arende.isSaving = true;
          store.arende.error = null;
        });
        const arendeResponse = await apiClient.createArbetsgivarearende(
          creationInfo.puzzelId,
          creationInfo.oragnisationsnummer,
          creationInfo.kontaktorsakId,
          creationInfo.kontaktorsakNamn,
          creationInfo.fortydligadKontaktorsak,
          creationInfo.samtalsamneIds,
          creationInfo.internalNote,
          creationInfo.isClosed,
          creationInfo.isVerified,
          creationInfo.kundgrupp,
          creationInfo.arendetyp,
          creationInfo.lamnaOverTill ?? LamnaOverType.Nej,
          creationInfo.kanal,
          creationInfo.ombud,
          creationInfo.namn,
          creationInfo.email,
          creationInfo.telefonnummer,
        );

        return arendeResponse.value;
      } catch (e) {
        handleError(e, this.$state);
      } finally {
        this.$patch((store) => {
          store.arende.isSaving = false;
        });
      }
    },
    async updateArende(
      updateInfo: UpdateArendeInfo,
      apiClient: IKundserviceAppApiClient,
    ) {
      try {
        this.$patch((store) => {
          store.arende.isSaving = true;
          store.arende.error = null;
        });
        const arendeResponse =
          await apiClient.updateArbetsgivarearendeByArendeNumber(
            updateInfo.arendeNumber,
            updateInfo.puzzelId,
            updateInfo.samtalsamneIds,
            updateInfo.internKommentar,
            updateInfo.isClosed,
            updateInfo.lamnaOverTill,
            updateInfo.namn,
            updateInfo.telefonnummer,
            updateInfo.email,
          );

        return await this.fetchArende(arendeResponse.value!.id, apiClient);
      } catch (e) {
        handleError(e, this.$state);
      } finally {
        this.$patch((store) => {
          store.arende.isSaving = false;
        });
      }
    },
    async reopenArende(
      reopenInfo: ReopenArendeInfo,
      apiClient: IKundserviceAppApiClient,
    ) {
      try {
        this.$patch((store) => {
          store.arende.isSaving = true;
          store.arende.error = null;
        });

        const reopenedArende =
          await apiClient.updateArbetsgivarearendeByArendeNumber(
            reopenInfo.arendeNumber,
            undefined,
            undefined,
            undefined,
            false,
            undefined,
            undefined,
            undefined,
            undefined,
          );

        return await this.fetchArende(reopenedArende.value!.id, apiClient);
      } catch (e) {
        handleError(e, this.$state);
      } finally {
        this.$patch((store) => {
          store.arende.isSaving = false;
        });
      }
    },
    async updateInternKommentar(
      internKommentarId: string,
      body: string,
      apiClient: IKundserviceAppApiClient,
    ) {
      try {
        this.$patch((store) => {
          store.internKommentar.isSaving = true;
          store.internKommentar.error = null;
        });

        const response = await apiClient.updateInternKommentar(
          internKommentarId,
          body,
        );

        this.$patch((store) => {
          store.internKommentar.data = null;
          store.arende.data!.internaKommentarer =
            store.arende.data!.internaKommentarer?.map((x) => {
              if (x.id === response.value!.id) {
                return response.value!;
              }
              return x;
            });
        });
      } catch (e) {
        console.error({ e });
        this.$patch((store) => {
          store.internKommentar.error = DEFAULT_API_ERROR_MESSAGE;
        });
      } finally {
        this.$patch((store) => {
          store.internKommentar.isSaving = false;
        });
      }
    },
  },
});

function handleError(e: unknown, state: State) {
  if (e instanceof ValueResult_1OfGuid && e.errors?.length > 0) {
    const error = e;

    state.arende.error = {
      status: error.status,
      errors: errorTypeToMessage(error.errors[0]),
    };
  } else {
    state.arende.error = {
      errors: DEFAULT_API_ERROR_MESSAGE,
    };
    console.error({ e });
  }
}
